import { useDevToolsStore } from '@frontend/devtools';
import { useLocationGroupStore } from '@frontend/scope';
import { createShallowStore, createStore } from '@frontend/store';
import { getTimezoneAbbreviation, getUserTimezone, isUserTimezoneOffsetDifferent } from '../helpers';

export type LocationTimezoneMap = Record<
  string,
  { timezone: string; timezoneAbbr: string; isUserOutsideTimezone: boolean }
>;

interface ScheduleLocationTimezoneStore {
  userTimezone: string;
  locationTimezoneMap: LocationTimezoneMap;
  setLocationTimezoneMap: (locationTimezoneMap: LocationTimezoneMap) => void;
  refreshUserTimezone: () => void;
}

const useScheduleLocationTimezoneStore = createStore<ScheduleLocationTimezoneStore>(
  (set) => ({
    // user timezone
    userTimezone: getUserTimezone(),
    refreshUserTimezone: () => {
      set({ userTimezone: getUserTimezone() });
    },
    // location timezone map
    locationTimezoneMap: {},
    setLocationTimezoneMap: (locationTimezoneMap) => {
      set({ locationTimezoneMap });
    },
  }),
  {
    name: 'schedule-location-timezone-store',
  }
);

export const useScheduleLocationTimezoneShallowStore = createShallowStore<ScheduleLocationTimezoneStore>(
  useScheduleLocationTimezoneStore
);

const updateLocationTimezoneMap = () => {
  const { selectedLocationIds, locationData } = useLocationGroupStore.getState();

  const locationTimezoneMap = selectedLocationIds?.reduce<LocationTimezoneMap>((acc, locationId) => {
    const timezone = locationData?.[locationId]?.timezone ?? '';
    if (timezone && locationId) {
      const userTimezone = useScheduleLocationTimezoneStore.getState().userTimezone;
      const isUserOutsideTimezone = isUserTimezoneOffsetDifferent(userTimezone, timezone);
      const timezoneAbbr = getTimezoneAbbreviation(timezone);
      acc[locationId] = { timezone, timezoneAbbr, isUserOutsideTimezone };
    }
    return acc;
  }, {});

  useScheduleLocationTimezoneStore.getState().setLocationTimezoneMap(locationTimezoneMap ?? {});
};

useLocationGroupStore.subscribe((state) => state.locationData, updateLocationTimezoneMap);
useLocationGroupStore.subscribe((state) => state.selectedLocationIds, updateLocationTimezoneMap);

useDevToolsStore.subscribe(() => {
  useScheduleLocationTimezoneStore.getState().refreshUserTimezone();
  updateLocationTimezoneMap();
});
